import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Search, Trash2, X } from "lucide-react";
import AppShell from "./AppShell";
import { api } from "../lib/api";

export default function WorkspaceRecordsPage({ resource, eyebrow, title, description, fields }) {
  const [records, setRecords] = useState([]);
  const [query, setQuery] = useState("");
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const response = await api.get(`/workspace/${resource}`, { params: { q: query }, withCredentials: true });
      setRecords(response.data);
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos cargar este módulo.");
    }
  }, [query, resource]);

  useEffect(() => {
    const timeout = window.setTimeout(refresh, 200);
    return () => window.clearTimeout(timeout);
  }, [refresh]);

  const openNew = () => {
    setForm({});
    setEditing(null);
    setError("");
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();
    setError("");
    try {
      if (editing) {
        await api.put(`/workspace/${resource}/${editing.id}`, form, { withCredentials: true });
      } else {
        await api.post(`/workspace/${resource}`, form, { withCredentials: true });
      }
      setOpen(false);
      setNotice("Cambios guardados.");
      refresh();
    } catch (issue) {
      setError(issue.response?.data?.detail || "Revisa los datos e intenta otra vez.");
    }
  };

  const remove = async (record) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await api.delete(`/workspace/${resource}/${record.id}`, { withCredentials: true });
      setNotice("Registro eliminado.");
      refresh();
    } catch (issue) {
      setError(issue.response?.data?.detail || "No pudimos eliminar el registro.");
    }
  };

  const visibleFields = useMemo(() => fields.slice(0, 3), [fields]);

  return (
    <AppShell>
      <main className="page records-page">
        <header className="records-hero"><div><small className="eyebrow">{eyebrow}</small><h1>{title.split(" ")[0]}<br /><em>{title.split(" ").slice(1).join(" ")}</em></h1><p>{description}</p></div><button className="records-add-button" data-testid={`${resource}-add-button`} onClick={openNew}><Plus size={18} />Agregar</button></header>
        <section className="records-toolbar"><label htmlFor={`${resource}-search`}><Search size={18} /><input id={`${resource}-search`} data-testid={`${resource}-search-input`} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar" /></label><span data-testid={`${resource}-count`}>{records.length} guardados</span></section>
        {notice && <p className="clients-notice" data-testid={`${resource}-notice`}>{notice}</p>}
        {error && <p className="clients-error" data-testid={`${resource}-error`}>{error}</p>}
        <section className="records-list" data-testid={`${resource}-list`}>
          {records.map((record) => <article key={record.id}><div>{visibleFields.map((field) => <p key={field.key}><small>{field.label}</small><b>{record[field.key] ?? "Pendiente"}</b></p>)}</div><aside><button data-testid={`${resource}-edit-${record.id}`} onClick={() => { setEditing(record); setForm(record); setOpen(true); }} aria-label="Editar"><Pencil size={17} /></button><button data-testid={`${resource}-delete-${record.id}`} onClick={() => remove(record)} aria-label="Eliminar"><Trash2 size={17} /></button></aside></article>)}
          {!records.length && <p className="empty" data-testid={`${resource}-empty`}>Aún no hay registros. Agrega el primero cuando estés listo.</p>}
        </section>
        {open && <div className="income-modal-overlay" data-testid={`${resource}-form-modal`}><form className="income-modal income-modal-form" onSubmit={save}><button className="modal-close" data-testid={`${resource}-form-close`} type="button" onClick={() => setOpen(false)} aria-label="Cerrar"><X /></button><h2>{editing ? "Editar" : "Agregar"}</h2>{fields.map((field) => <label htmlFor={`${resource}-${field.key}`} key={field.key}>{field.label}{field.type === "select" ? <select id={`${resource}-${field.key}`} data-testid={`${resource}-${field.key}-input`} value={form[field.key] || ""} onChange={(event) => setForm({ ...form, [field.key]: event.target.value })}>{field.options.map((option) => <option key={option}>{option}</option>)}</select> : field.type === "textarea" ? <textarea id={`${resource}-${field.key}`} data-testid={`${resource}-${field.key}-input`} value={form[field.key] || ""} onChange={(event) => setForm({ ...form, [field.key]: event.target.value })} required={field.required} /> : <input id={`${resource}-${field.key}`} data-testid={`${resource}-${field.key}-input`} type={field.type || "text"} min={field.type === "number" ? "0" : undefined} value={form[field.key] || ""} onChange={(event) => setForm({ ...form, [field.key]: event.target.value })} required={field.required} />}</label>)}<button className="primary" data-testid={`${resource}-save-button`}>Guardar</button></form></div>}
      </main>
    </AppShell>
  );
}